import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Meta } from '@angular/platform-browser';
import { Task } from '@src/app/models/task';
import { Project } from '@src/app/models/project';
import { Customer } from '@src/app/models/customer';
import { PersonOrganization } from '@src/app/models/personorganization';
import { DatastoreService } from '@src/app/helpers/datastore/datastore.service';
import { AdhoctaskService } from './service/adhoctask.service';
import { AuthorizationService } from '@src/app/helpers/authorization.service';
import { SessionHelper } from '@src/app/helpers/sessionHelper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TimeregistrationService } from '@src/app/helpers/timeregistration/timeregistration.service';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-adhoc',
  templateUrl: './adhoc.component.html',
  styleUrls: ['./adhoc.component.css']
})
export class AdhocComponent implements OnInit {
  adhocTasks: Task[] = [];
  orgMembers: PersonOrganization[] = [];
  projectList: Project[] = [];
  customerList: Customer[] = [];
  loading = false;
  showForm = false;
  formTask: Task;
  errorMessage: string;
  timeregEnabled = false;
  newTaskEstimateHours: number;

  constructor(
    private datastore: DatastoreService,
    private adhoctaskService: AdhoctaskService,
    private authservice: AuthorizationService,
    private router: Router,
    private meta: Meta,
    private timeregSrv: TimeregistrationService,
  ) { }

  ngOnInit(): void {
    this.meta.addTags([
      { name: 'robots', content: 'noindex' },
      { name: 'googlebot', content: 'noindex' },
    ]);

    if (!this.authservice.isAuthorized('org_edit', SessionHelper.getChoosenOrg())) {
      this.router.navigate(['/portal']);
      return;
    }

    this.timeregEnabled = SessionHelper.hasModule('timereg');

    this.datastore.orgMembers$.pipe(untilDestroyed(this)).subscribe(members => {
      this.orgMembers = members;
    });

    this.datastore.projectList$.pipe(untilDestroyed(this)).subscribe(projects => {
      this.projectList = projects;
    });

    this.datastore.customers$.pipe(untilDestroyed(this)).subscribe(customers => {
      this.customerList = customers;
    });

    this.loadTasks();
  }

  loadTasks() {
    this.loading = true;
    this.datastore.getAdhocTasks().pipe(untilDestroyed(this)).subscribe({
      next: (tasks) => {
        this.adhocTasks = tasks || [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  memberName(personId: string): string {
    const member = this.orgMembers.find(m => m.personId === personId);
    return member ? member.membername : personId;
  }

  projectName(projectId: string): string {
    if (!projectId) {
      return '';
    }
    const project = this.projectList.find(p => p.projectId === projectId);
    return project ? project.title : '';
  }

  customerName(customerId: string): string {
    if (!customerId) {
      return '';
    }
    const customer = this.customerList.find(c => c.customerId.split('_')[0] === customerId.split('_')[0]);
    return customer ? customer.title : '';
  }

  openCreate() {
    this.formTask = new Task();
    this.formTask.tasktype = 'adhoc';
    this.formTask.taskstate = 'waiting';
    this.errorMessage = null;
    this.newTaskEstimateHours = undefined;
    this.showForm = true;
  }

  openEdit(task: Task) {
    this.formTask = { ...task } as Task;
    this.errorMessage = null;
    this.showForm = true;
  }

  closeForm() {
    this.showForm = false;
    this.formTask = null;
    this.errorMessage = null;
  }

  saveTask() {
    if (!this.formTask.title || !this.formTask.responsible) {
      this.errorMessage = 'modalvalidation.requiredfieldmissing';
      return;
    }

    const isNew = !this.formTask.taskId;
    const request = isNew
      ? this.adhoctaskService.createAdhocTask(this.formTask)
      : this.adhoctaskService.updateAdhocTask(this.formTask);

    const initialEstimateHours = this.newTaskEstimateHours;

    request.pipe(untilDestroyed(this)).subscribe((result) => {
      // Initial estimate (timereg org module): posted right after
      // creation, since the task needs a taskId before it can carry one.
      if (isNew && this.timeregEnabled && initialEstimateHours > 0) {
        const newTaskId = String(result);
        const minutes = Math.round(initialEstimateHours * 60);
        this.timeregSrv.addAdhocTaskEstimate(newTaskId, minutes)
          .pipe(untilDestroyed(this))
          .subscribe({
            error: (err) => console.error('[Adhoc] Failed to set initial estimate', err),
          });
      }
      this.closeForm();
      this.loadTasks();
    });
  }

  deleteTask(task: Task) {
    this.adhoctaskService.deleteAdhocTask(task).pipe(untilDestroyed(this)).subscribe(() => {
      this.loadTasks();
    });
  }

  toggleDone(task: Task) {
    const updated = { ...task, taskstate: task.taskstate === 'Done' ? 'Undone' : 'Done' } as Task;
    this.adhoctaskService.updateAdhocTask(updated).pipe(untilDestroyed(this)).subscribe(() => {
      this.loadTasks();
    });
  }
}
